// Regras de validação de parcelas na Alteração de Venda, portadas do sistema legado
// (SZ Software / FRM_CAD_VENDAALTERACAO.pas — procedures VerificaParcelas, GridParcelasCellClick
// e os checks de reconciliação que rodam antes de cada gravação em adicionarParcelasV2 /
// refazerParcelasV2). Mantidas em JS puro e sem estado — só recebem dados e devolvem resultado.
//
// Convenção de status (idêntica ao banco): ATIVO = 'SIM' → pendente/em aberto,
// 'NAO' → já pago/baixado, 'DEL' → soft delete.

const FORMAS_LIQUIDADAS_NO_ATO = [3, 4, 5]; // Pix, Cartão Débito, Cartão Crédito
const ID_FORMA_DINHEIRO = 1;

/** Cartão/Pix são liquidados no ato — o legado proíbe deixá-los pendentes. */
export function isFormaLiquidadaNoAto(idForma, formaNome) {
	const nomeUpper = (formaNome || "").toUpperCase();
	return (
		FORMAS_LIQUIDADAS_NO_ATO.includes(Number(idForma)) ||
		nomeUpper.includes("PIX") ||
		nomeUpper.includes("CARTAO") ||
		nomeUpper.includes("CARTÃO")
	);
}

/** Dinheiro é a única forma que o operador pode alternar entre pago e pendente. */
export function isFormaDinheiro(idForma, formaNome) {
	return (
		Number(idForma) === ID_FORMA_DINHEIRO ||
		(formaNome || "").toUpperCase().includes("DINHEIRO")
	);
}

/**
 * Valida a lista de parcelas antes de montar o payload.
 * Espelha TCad_VendaAlteracao.VerificaParcelas + a trava de forma de pagamento.
 *
 * @returns {string|null} mensagem do primeiro erro encontrado, ou null se estiver tudo certo.
 */
export function validarParcelas(parcelas = []) {
	if (!Array.isArray(parcelas) || parcelas.length === 0) {
		return "Gere as parcelas antes de confirmar.";
	}

	const hojeStr = new Date().toISOString().split("T")[0];

	for (let i = 0; i < parcelas.length; i++) {
		const p = parcelas[i];
		const rotulo = `Parcela ${p.parcela ?? i + 1}`;

		// Valor zerado — legado: 'O Valor não pode ser igual a zero!'
		if (!Number(p.valor)) {
			return `${rotulo}: o valor não pode ser igual a zero.`;
		}

		if (Number(p.valor) < 0) {
			return `${rotulo}: o valor não pode ser negativo.`;
		}

		const dataParcela = p.data || p.data_vencimento || p.dataparcela;
		const estaPaga = p.ativo === "NAO" || p.ativo === false;

		// Receita efetivada não pode estar no futuro — legado: 'Não é possível inserir uma
		// receita efetivada em uma data futura, por favor altere o ativo e tente novamente.'
		if (estaPaga && dataParcela && dataParcela > hojeStr) {
			return `${rotulo}: uma parcela marcada como paga não pode ter data futura (${dataParcela
				.split("-")
				.reverse()
				.join("/")}). Ajuste a data ou marque como pendente.`;
		}

		// Cartão/Pix não podem ficar pendentes — legado: 'Não é possível colocar uma parcela
		// com essa forma de pagamento como pendente!'
		if (!estaPaga && isFormaLiquidadaNoAto(p.id_forma, p.forma || p.forma_pagamento)) {
			return `${rotulo}: ${p.forma || p.forma_pagamento} é liquidado no ato e não pode ficar pendente.`;
		}
	}

	return null;
}

/**
 * Reconciliação obrigatória antes de gravar: a soma do que vai ser faturado precisa bater
 * exatamente com o valor esperado. É o mesmo guard-rail que o legado repete em todos os
 * fluxos de gravação (`if StrToCurr(CALC) <> BRUTO then raise Exception`).
 *
 * @returns {string|null} mensagem de erro, ou null se reconciliar.
 */
export function validarReconciliacao(parcelas = [], valorEsperado = 0) {
	const somaCentavos = (parcelas || []).reduce(
		(acc, p) => acc + Math.round(Number(p.valor || 0) * 100),
		0,
	);
	const esperadoCentavos = Math.round(Number(valorEsperado) * 100);

	// Tolerância de 1 centavo, mesma margem usada no legado (Abs(...) > 0.01)
	if (Math.abs(somaCentavos - esperadoCentavos) > 1) {
		const fmt = (c) =>
			(c / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
		return `A soma das parcelas (${fmt(somaCentavos)}) não corresponde ao valor a faturar (${fmt(
			esperadoCentavos,
		)}). Corrija antes de confirmar.`;
	}

	return null;
}

/**
 * Trava do abatimento parcial: não dá pra abater mais do que o total ainda em aberto.
 * Espelha o guard do legado em baixaparcial ('Valor Insuficiente Para Realizar Operação!!!').
 *
 * @returns {string|null} mensagem de erro, ou null se houver saldo suficiente.
 */
export function validarSaldoParaAbatimento(parcelasAtuais = [], valorAbater = 0) {
	const pendentes = (parcelasAtuais || []).filter(
		(p) => p.ativo === "SIM" || p.ativo === true || p.ativo === 1,
	);

	if (pendentes.length === 0) {
		return "Não há parcelas em aberto para abater a diferença. Escolha refazer o parcelamento.";
	}

	const totalPendenteCentavos = pendentes.reduce(
		(acc, p) => acc + Math.round(Number(p.valor || 0) * 100),
		0,
	);
	const abaterCentavos = Math.round(Math.abs(Number(valorAbater)) * 100);

	if (abaterCentavos > totalPendenteCentavos) {
		const fmt = (c) =>
			(c / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
		return `O valor a abater (${fmt(abaterCentavos)}) é maior que o total em aberto (${fmt(
			totalPendenteCentavos,
		)}). Escolha refazer o parcelamento.`;
	}

	return null;
}

/**
 * Distribui o abatimento sequencialmente entre as parcelas em aberto, na ordem em que vêm —
 * mesma mecânica de TCad_VendaAlteracao.baixaparcial: vai consumindo parcela a parcela;
 * se o valor a abater cobre a parcela inteira, ela é quitada; se sobra saldo dentro dela,
 * ela permanece em aberto com o valor reduzido.
 *
 * Só monta o "de → para" pro backend aplicar — não grava nada.
 */
export function calcularAbatimentoSequencial(parcelasAtuais = [], valorAbater = 0) {
	let restanteCentavos = Math.round(Math.abs(Number(valorAbater)) * 100);
	const ajustes = [];

	for (const p of parcelasAtuais) {
		if (restanteCentavos <= 0) break;

		const estaPendente = p.ativo === "SIM" || p.ativo === true || p.ativo === 1;
		if (!estaPendente) continue;

		const valorCentavos = Math.round(Number(p.valor || 0) * 100);
		if (valorCentavos <= 0) continue;

		if (restanteCentavos >= valorCentavos) {
			// Abatimento cobre a parcela inteira → quita ela
			ajustes.push({
				id_conta_entrada: p.id_conta_entrada,
				parcela: p.parcela,
				valor_anterior: valorCentavos / 100,
				valor_novo: 0,
				ativo: "DEL",
				abatido: valorCentavos / 100,
			});
			restanteCentavos -= valorCentavos;
		} else {
			// Sobra saldo dentro da parcela → reduz o valor e mantém em aberto
			ajustes.push({
				id_conta_entrada: p.id_conta_entrada,
				parcela: p.parcela,
				valor_anterior: valorCentavos / 100,
				valor_novo: (valorCentavos - restanteCentavos) / 100,
				ativo: "SIM",
				abatido: restanteCentavos / 100,
			});
			restanteCentavos = 0;
		}
	}

	return {
		ajustes,
		restanteNaoAbatido: restanteCentavos / 100,
	};
}
